import { enableNewReviewSaveModal, enablePredictiveTargetingApi } from '@gonfalon/dogfood-flags/dogfood-flags';
import { ContextualFeedbackTrigger } from '@gonfalon/feedback/ContextualFeedbackTrigger';
import { useHasFlagEvaluated } from '@gonfalon/flags/hooks/useHasFlagEvaluated';
import {
  ButtonGroup,
  IconButton,
  Perceivable,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  TooltipTrigger,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import type { ApprovalRequestResourceKind } from '#utils/approvals/types';
import { trackFlagTargetingEvent } from '#utils/flags/flagAnalytics';

import { useReviewAndSaveContext } from './reviewAndSaveContext';

interface ToolbarProps {
  resourceKind: ApprovalRequestResourceKind;
  resourceContext: {
    projectKey: string;
    resourceKey: string;
    environmentKey: string;
  };
  hasConflicts?: boolean;
  isApprovalRequest?: boolean;
  hideJsonDiff?: boolean;
  hideTestRun?: boolean;
}

export function Toolbar({
  resourceKind,
  resourceContext,
  isApprovalRequest = false,
  hasConflicts = false,
  hideJsonDiff = false,
  hideTestRun = false,
}: ToolbarProps) {
  const { isJsonDiffingEnabled, setIsJsonDiffingEnabled, expandAll, setExpandAll, testRunEnabled, setTestRunEnabled } =
    useReviewAndSaveContext();
  const { projectKey, resourceKey, environmentKey } = resourceContext;
  const isFlagConfiguration = resourceKind === 'FlagConfiguration';

  const flagHasEvaluated = useHasFlagEvaluated({
    projectKey,
    flagKey: resourceKey,
    environmentKey,
    enabled: isFlagConfiguration,
  });

  if (!enableNewReviewSaveModal()) {
    return null;
  }

  const description = expandAll ? 'Collapse all changes' : 'Expand all changes';

  return (
    <ButtonGroup>
      {!isApprovalRequest && (
        <ContextualFeedbackTrigger size="small" variant="minimal" flagKey="enable-new-review-save-modal" />
      )}
      {!hideJsonDiff && (
        <ToggleButtonGroup
          selectionMode="single"
          selectedKeys={testRunEnabled ? [] : [isJsonDiffingEnabled ? 'json-editing' : 'ui-editing']}
          data-test-id="schema-editor-mode-button-group"
        >
          <ToggleButton
            size="small"
            onPress={() => {
              setIsJsonDiffingEnabled(false);
              setTestRunEnabled(false);
              trackFlagTargetingEvent('View Visual Button Clicked', {
                isApprovalRequest,
              });
            }}
            id="ui-editing"
            aria-label="UI editing"
            data-test-id="instruction-mode-button-visual"
          >
            <Icon size="tiny" name="clipboard-edit" />
            Visual
          </ToggleButton>
          <Perceivable isDisabled={hasConflicts}>
            <TooltipTrigger isDisabled={!hasConflicts}>
              <ToggleButton
                size="small"
                onPress={() => {
                  setIsJsonDiffingEnabled(true);
                  setTestRunEnabled(false);
                  trackFlagTargetingEvent('View JSON Button Clicked', {
                    isApprovalRequest,
                  });
                }}
                id="json-editing"
                aria-label="JSON editing"
                data-test-id="instruction-mode-button-json"
              >
                <Icon size="tiny" name="code-circle-outline" />
                JSON
              </ToggleButton>
              <Tooltip placement="bottom">Cannot render JSON with conflicts</Tooltip>
            </TooltipTrigger>
          </Perceivable>
        </ToggleButtonGroup>
      )}

      <TooltipTrigger>
        <IconButton
          aria-label={description}
          icon={expandAll ? 'chevrons-in' : 'chevrons-out'}
          size="small"
          isDisabled={testRunEnabled || isJsonDiffingEnabled}
          onPress={() => {
            setExpandAll(!expandAll);
            trackFlagTargetingEvent(description, {
              isApprovalRequest,
            });
          }}
        />
        <Tooltip placement="bottom">{description}</Tooltip>
      </TooltipTrigger>

      {enablePredictiveTargetingApi() && isFlagConfiguration && !hideTestRun && (
        <ToggleButtonGroup
          selectionMode="single"
          selectedKeys={testRunEnabled ? ['test-run'] : []}
          data-test-id="schema-editor-mode-button-group"
        >
          <Perceivable isDisabled={hasConflicts}>
            <TooltipTrigger isDisabled={!hasConflicts}>
              <ToggleButton
                size="small"
                onPress={() => {
                  setTestRunEnabled(true);
                  setIsJsonDiffingEnabled(false);
                  trackFlagTargetingEvent('View Test Run Button Clicked', {
                    isApprovalRequest,
                    flagHasEvaluated,
                  });
                }}
                id="test-run"
              >
                <Icon size="tiny" name="fingerprint" />
                Test run
              </ToggleButton>
              <Tooltip placement="bottom">Cannot run test run with conflicts</Tooltip>
            </TooltipTrigger>
          </Perceivable>
        </ToggleButtonGroup>
      )}
    </ButtonGroup>
  );
}
